import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Input, List, Tag, Space, message, Popconfirm, Tooltip, Modal, Empty, Dropdown } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, PushpinOutlined, PushpinFilled, ReloadOutlined, DownOutlined, SaveOutlined } from '@ant-design/icons';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { zh } from '@blocknote/core/locales';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import { apiFetch, getToken } from '../utils/api.js';

// 生成当天标题，如「2026-09-02 运营日记」
const todayTitle = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} 运营日记`;
};

// 运营日报模板（BlockNote 块结构）：今日订单 / 营收利润 / 今日待办 / 明日计划
const T = (text) => ({ type: 'text', text, styles: {} });
const DIARY_TEMPLATE = [
  { type: 'heading', props: { level: 2 }, content: [T('📦 今日订单')] },
  { type: 'bulletListItem', content: [T('订单数：')] },
  { type: 'bulletListItem', content: [T('发货：')] },
  { type: 'heading', props: { level: 2 }, content: [T('💰 营收与利润')] },
  { type: 'checkListItem', content: [T('今日营收：')] },
  { type: 'checkListItem', content: [T('今日利润：')] },
  { type: 'heading', props: { level: 2 }, content: [T('✅ 今日待办')] },
  { type: 'checkListItem', content: [T('订单发货')] },
  { type: 'checkListItem', content: [T('库存盘点')] },
  { type: 'checkListItem', content: [T('售后跟进')] },
  { type: 'heading', props: { level: 2 }, content: [T('📌 明日计划')] },
  { type: 'checkListItem', content: [T('补货计划')] },
  { type: 'checkListItem', content: [T('活动准备')] },
];

export default function OperationDiary() {
  const [docs, setDocs] = useState([]);
  const [cur, setCur] = useState(null);            // { id, title }
  const curRef = useRef(null);                      // 始终指向最新 cur，避免自动保存闭包串到旧文档
  const titleRef = useRef('');                      // 当前文档标题（供自动保存/卸载兜底读取）
  const [curTitle, setCurTitle] = useState('');     // 顶部标题输入框受控值
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null); // 上次自动保存时间（Date）
  const [renameModal, setRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // 要改名的文档 {id,title}，避免误改当前文档
  const [newTitle, setNewTitle] = useState('');
  const saveTimer = useRef(null);

  // BlockNote 编辑器实例（只创建一次，切换文档用 replaceBlocks 加载，避免失焦/性能问题）；dictionary=zh 让界面全中文
  const editor = useCreateBlockNote({
    dictionary: zh,
    // 图片上传：本地图片 → 后端 /api/upload/image → 返回 URL 插入
    uploadFile: async (file) => {
      try {
        const form = new FormData();
        form.append('image', file);
        const token = getToken();
        const res = await fetch('/api/upload/image', {
          method: 'POST', body: form,
          headers: token ? { Authorization: 'Bearer ' + token } : {},
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || '上传失败');
        return data.url;
      } catch (e) {
        message.error('图片上传失败: ' + (e && e.message ? e.message : e));
        throw e;
      }
    },
  });

  const loadDocs = useCallback(() => {
    apiFetch('/api/docs').then(r => r.json()).then(d => setDocs(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { curRef.current = cur; }, [cur]);

  // 保存当前文档（silent=true 静默，用于自动保存/切换兜底）
  const doSave = useCallback(async (silent = false) => {
    const c = curRef.current;
    if (!c || !editor) return;
    if (!silent) setSaving(true);
    try {
      const content = editor.document;
      const res = await apiFetch('/api/docs/' + c.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, title: titleRef.current }),
      }).then(r => r.json());
      if (res.success) {
        setLastSaved(new Date());
        if (!silent) message.success('已保存');
      } else if (!silent) message.error(res.error || '保存失败');
      loadDocs();
    } catch (e) { if (!silent) message.error('保存失败'); }
    if (!silent) setSaving(false);
  }, [editor, loadDocs]);

  // 内容变更 → 防抖 1.5s 自动保存
  useEffect(() => {
    if (!editor) return;
    const unsub = editor.onChange(() => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(true), 1500);
    });
    return () => { try { unsub && unsub(); } catch (e) {} };
  }, [editor, doSave]);

  // 组件卸载前：兜底保存当前文档（防刷新/关页丢失最后 1.5s 内的编辑）
  useEffect(() => () => {
    const c = curRef.current;
    if (!c || !editor) return;
    try {
      const content = editor.document;
      apiFetch('/api/docs/' + c.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, title: titleRef.current }),
      });
    } catch (e) {}
  }, [editor]);

  // 标题变更 → 防抖保存（标题随内容一起存）
  const onTitleChange = (v) => {
    setCurTitle(v);
    titleRef.current = v;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(true), 1500);
  };

  // 打开文档
  const openDoc = async (d) => {
    try {
      // 切换前：清除防抖定时器，并立即保存当前文档（避免编辑未保存就切走导致丢失）
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      if (editor && curRef.current && String(curRef.current.id) !== String(d.id)) {
        try { await doSave(true); } catch (e) {}
      }
      const r = await apiFetch('/api/docs/' + d.id).then(r => r.json());
      if (!r.data) { message.error(r.error || '加载失败'); return; }
      setCur({ id: d.id, title: d.title });
      setCurTitle(d.title);
      titleRef.current = d.title;
      setLastSaved(null);
      // 加载正文（BlockNote JSON）
      const content = r.data.content;
      if (editor) {
        try {
          editor.replaceBlocks(editor.document, Array.isArray(content) ? content : []);
        } catch (e) { message.error('正文加载失败: ' + (e && e.message ? e.message : e)); }
      }
    } catch (e) { message.error('加载失败'); }
  };

  // 新建：自动按当天日期起标题（同名自动加序号）；withTemplate=true 预填运营日报模板
  const createDoc = async (withTemplate = false) => {
    const base = todayTitle();
    let title = base, i = 2;
    while (docs.some(x => x.title === title)) title = `${base} (${i++})`;
    const body = { title };
    if (withTemplate) body.content = DIARY_TEMPLATE;
    const res = await apiFetch('/api/docs', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());
    if (res.success) {
      message.success(withTemplate ? '运营日报已创建' : '日记已创建');
      loadDocs();
      openDoc(res.data);
    } else message.error(res.error || '创建失败');
  };

  const renameDoc = async () => {
    if (!renameTarget) return;
    const t = newTitle.trim();
    if (!t) { message.warning('标题不能为空'); return; }
    const res = await apiFetch('/api/docs/' + renameTarget.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: t }),
    }).then(r => r.json());
    if (res.success) {
      message.success('已改名');
      setRenameModal(false);
      setCur(c => (c && c.id === renameTarget.id ? { ...c, title: t } : c));
      if (curRef.current && curRef.current.id === renameTarget.id) { setCurTitle(t); titleRef.current = t; }
      setRenameTarget(null);
      loadDocs();
    } else message.error(res.error || '改名失败');
  };

  const delDoc = async (d) => {
    const res = await apiFetch('/api/docs/' + d.id, { method: 'DELETE' }).then(r => r.json());
    if (res.success) {
      message.success('已删除');
      if (cur && cur.id === d.id) {
        setCur(null); curRef.current = null;
        setCurTitle(''); titleRef.current = '';
        if (editor) { try { editor.replaceBlocks(editor.document, []); } catch (e) {} }
      }
      loadDocs();
    } else message.error(res.error || '删除失败');
  };

  // 切换置顶（不触发内容编辑的 updated_at，后端 PUT 已做对应处理）
  const togglePin = async (d, e) => {
    e.stopPropagation();
    const res = await apiFetch('/api/docs/' + d.id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned: !d.pinned }),
    }).then(r => r.json());
    if (res.success) loadDocs();
  };

  return (
    <div className="page-container" style={{ display: 'flex', gap: 16, alignItems: 'stretch', height: 'calc(100vh - 140px)' }}>
      {/* 左侧：日记列表 */}
      <Card size="small" className="diary-solid" style={{ width: 260, flex: '0 0 260px', overflow: 'auto' }}
        title={<span style={{ fontSize: 14 }}>📝 运营日记</span>}
        extra={
          <Space size={4}>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadDocs} title="刷新" />
            <Dropdown
              menu={{
                items: [
                  { key: 'blank', label: '新建空白日记' },
                  { key: 'daily', label: '新建运营日报（带模板）' },
                ],
                onClick: ({ key }) => createDoc(key === 'daily'),
              }}
              trigger={['click']}
            >
              <Button size="small" type="primary">
                <PlusOutlined /> 新建 <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        }>
        <List
          size="small" dataSource={docs} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无日记，点「新建」开始记录" /> }}
          renderItem={d => (
            <List.Item
              style={{
                cursor: 'pointer', padding: '6px 4px',
                background: cur && cur.id === d.id ? '#FAEEDA' : (d.pinned ? '#FFF7F3' : 'transparent'),
                borderRadius: 8,
                border: cur && cur.id === d.id ? '1px solid #EF9F27' : (d.pinned ? '1px solid #FFCBB6' : '1px solid var(--brand-border)'),
                marginBottom: 4,
              }}
              onClick={() => openDoc(d)}
              actions={[
                <Tooltip title={d.pinned ? '取消置顶' : '置顶'} key="p">
                  {d.pinned
                    ? <PushpinFilled style={{ fontSize: 13, color: 'var(--brand)' }} onClick={(e) => togglePin(d, e)} />
                    : <PushpinOutlined style={{ fontSize: 12, color: '#bfbfbf' }} onClick={(e) => togglePin(d, e)} />}
                </Tooltip>,
                <Tooltip title="改名" key="r"><EditOutlined style={{ fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setNewTitle(d.title); setRenameTarget(d); setRenameModal(true); }} /></Tooltip>,
                <Popconfirm key="del" title="删除这篇日记？" onConfirm={() => delDoc(d)}>
                  <span onClick={(e) => e.stopPropagation()}><DeleteOutlined style={{ fontSize: 12, color: '#cf1322' }} /></span>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={<span style={{ fontSize: 13, fontWeight: d.pinned ? 600 : 500, wordBreak: 'break-all' }}>
                  {d.pinned && <PushpinFilled style={{ fontSize: 11, color: 'var(--brand)', marginRight: 4 }} />}
                  {d.title}
                </span>}
                description={<span style={{ fontSize: 11, color: '#8c8c8c' }}>{d.created_at || d.updated_at || ''}</span>}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* 右侧：文档编辑器 */}
      <Card size="small" className="diary-solid" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#fff' }}
        title={
          cur ? (
            <Input
              value={curTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              maxLength={60}
              bordered={false}
              style={{ fontSize: 18, fontWeight: 700, color: '#26201C', padding: 0 }}
              placeholder="日记标题"
            />
          ) : (
            <span style={{ fontSize: 14, color: '#8c8c8c' }}>请选择或新建一篇日记</span>
          )
        }
        extra={
          <Space wrap>
            {cur && (
              <>
                <Button size="small" icon={<SaveOutlined />} loading={saving} onClick={() => doSave(false)}>保存</Button>
                <Tag color="#FAEEDA" style={{ color: 'var(--brand-strong)', fontSize: 12 }}>输入 / 唤起菜单插入标题·待办·表格等</Tag>
                <Tag color={saving ? '#FAEEDA' : '#EAF5E4'} style={{ marginInlineEnd: 0, fontSize: 12, color: saving ? 'var(--brand-strong)' : '#389E0D' }}>
                  {saving ? '保存中…' : (lastSaved ? '已自动保存 ' + lastSaved.toTimeString().slice(0, 8) : '自动保存已开启')}
                </Tag>
              </>
            )}
          </Space>
        }>
        <div style={{ flex: 1, overflow: 'auto', minHeight: 400, borderRadius: 8, position: 'relative' }}>
          <BlockNoteView editor={editor} theme="light" editable={!!cur} />
          {!cur && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', background: '#fff' }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请先点左上角「新建」创建一篇日记，再开始写内容" />
            </div>
          )}
        </div>
      </Card>

      {/* 改名弹窗 */}
      <Modal title="重命名日记" open={renameModal} onOk={renameDoc} onCancel={() => { setRenameModal(false); setRenameTarget(null); }} okText="保存" width={380}>
        <Input style={{ marginTop: 12 }} value={newTitle} onChange={e => setNewTitle(e.target.value)} maxLength={60} />
      </Modal>
    </div>
  );
}
